import { useState } from "react";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { centsToUsd } from "@/lib/money";
import { formatShortDate } from "@/features/events/eventDate";
import {
  INVOICE_KIND_LABELS,
  INVOICE_STATUS_BADGE_VARIANT,
  INVOICE_STATUS_LABELS,
} from "@/api/types/invoice";
import { usePortalInvoiceCheckout } from "./usePortal";
import type { PortalInvoice } from "./types";

interface PortalInvoicesCardProps {
  invoices: PortalInvoice[];
  token: string;
}

export function PortalInvoicesCard({ invoices, token }: PortalInvoicesCardProps) {
  if (invoices.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoices</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {invoices.map((invoice) => (
          <PortalInvoiceRow key={invoice.id} invoice={invoice} token={token} />
        ))}
      </CardContent>
    </Card>
  );
}

interface PortalInvoiceRowProps {
  invoice: PortalInvoice;
  token: string;
}

function PortalInvoiceRow({ invoice, token }: PortalInvoiceRowProps) {
  const [payError, setPayError] = useState<string | null>(null);
  const checkout = usePortalInvoiceCheckout(token);

  // Stripe checkout is only ever offered for an invoice that's actually
  // outstanding — mirrors the backend's own guard (paid/void both 400 on
  // POST /invoice/:id/checkout), so we never show a button that just fails.
  const canPay =
    invoice.amount_due_cents > 0 &&
    invoice.status !== "paid" &&
    invoice.status !== "void";

  const handlePay = () => {
    setPayError(null);
    checkout.mutate(invoice.id, {
      onSuccess: (url) => {
        window.location.href = url;
      },
      onError: (err) => setPayError(getErrorMessage(err)),
    });
  };

  return (
    <div
      data-invoice-id={invoice.id}
      className="flex flex-col gap-3 rounded-md border border-border p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-text-primary">
            {INVOICE_KIND_LABELS[invoice.kind]}
          </span>
          <span className="text-sm text-text-muted">#{invoice.number}</span>
          <Badge variant={INVOICE_STATUS_BADGE_VARIANT[invoice.status]}>
            {INVOICE_STATUS_LABELS[invoice.status]}
          </Badge>
        </div>
        {invoice.due_date && (
          <p className="mt-1 text-sm text-text-muted">
            Due {formatShortDate(invoice.due_date)}
          </p>
        )}
      </div>

      <div className="flex flex-col items-start gap-2 sm:items-end">
        <span className="tabular text-lg font-semibold text-text-primary">
          {centsToUsd(invoice.amount_due_cents)}
          {invoice.amount_due_cents > 0 && (
            <span className="ml-1 text-xs font-normal text-text-muted">due</span>
          )}
        </span>

        {invoice.status === "paid" ? (
          <Badge variant="success">Paid</Badge>
        ) : canPay ? (
          <Button size="sm" onClick={handlePay} disabled={checkout.isPending}>
            {checkout.isPending ? "Redirecting…" : "Pay now"}
          </Button>
        ) : null}

        {/* Read-only, portal-token-scoped download (GET /api/public/portal/
            invoice/:id/pdf, app/api/portal.py) — never the staff-only
            generate-and-persist route (POST /api/v2/invoices/:id/pdf). */}
        <a
          href={`/api/public/portal/invoice/${invoice.id}/pdf?token=${encodeURIComponent(token)}`}
          target="_blank"
          rel="noreferrer"
          className="text-sm font-medium text-primary hover:underline"
        >
          View PDF
        </a>

        {payError && (
          <p className="max-w-[16rem] text-right text-sm text-danger" role="alert">
            {payError}
          </p>
        )}
      </div>
    </div>
  );
}
